/**
 * Letter-template API client.
 *
 * Mirrors apps/api/src/modules/crm-marketing/templates.routes.ts.
 *
 * A template is COPIED into a campaign, never referenced live - which is why
 * there is no "campaigns using this template" list to keep in sync here, only a
 * count the list route computes. Editing a template must not rewrite letters
 * that already went out under the old wording.
 */
import { api } from "./api";

export interface TemplateRow {
  id: string;
  slug: string;
  /** Set when this row is the edited version of a letter that ships with the
   *  module. The library shows it in place of the shipped wording, and deleting
   *  the row is what restores the original. */
  presetId: string | null;
  /** Only read for a row that has a `presetId`: the shipped letter behind it
   *  has been taken off the shelf. Deleting the row puts it back. */
  hidden: boolean;
  name: string;
  description: string | null;
  locale: string;
  subject: string;
  preheader: string;
  /** The builder's block layout. Null for a template written straight in HTML.
   *  Present in the LIST too - it is the compact source, and the library
   *  renders a real preview of every card from it. */
  design: unknown | null;
  /** How many campaigns started from this template. Shown next to Delete,
   *  because that is the number that makes deleting it a decision. */
  usedBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface Template extends TemplateRow {
  bodyHtml: string;
  bodyText: string;
  createdBy: string | null;
}

const BASE = "/admin/crm/marketing/templates";

export const listTemplates = () => api.get<{ rows: TemplateRow[] }>(BASE);

export const getTemplate = (id: string) =>
  api.get<{ template: Template }>(`${BASE}/${encodeURIComponent(id)}`);

export const createTemplate = (body: Record<string, unknown>) =>
  api.post<{ template: Template }>(BASE, body);

export const patchTemplate = (id: string, body: Record<string, unknown>) =>
  api.patch<{ template: Template }>(`${BASE}/${encodeURIComponent(id)}`, body);

export const deleteTemplate = (id: string) =>
  api.del<{ ok: true }>(`${BASE}/${encodeURIComponent(id)}`);

/**
 * The editable row for a shipped letter, created on the first call and returned
 * as-is afterwards.
 *
 * `seed` is what the row starts as, and only the first call uses it: the presets
 * live here in the bundle, so the server cannot build the first version itself.
 * Calling this again never overwrites what the operator has since written.
 */
export const adoptPreset = (presetId: string, seed: Record<string, unknown>) =>
  api.post<{ template: Template; created: boolean }>(
    `${BASE}/preset/${encodeURIComponent(presetId)}`,
    seed,
  );
